import { Fragment, useMemo, useState } from "react";

import type { ApexGameEntry, ApexSeriesDetails, ApexStanding, LegacyMatch, TournamentResponse } from "../api/legacyTypes";
import { EntityLink } from "./EntityLink";
import { LocalDateTime } from "./LocalDateTime";

type Props = {
  stage: string;
  matches: LegacyMatch[];
  teams: TournamentResponse["tournament"]["teams"];
};

type Aggregate = ApexStanding & { outcome: "advance" | "stay" | "down" | "" };

const overallKey = "__overall__";

function withOutcomes(rows: ApexStanding[], rules?: ApexSeriesDetails, enabled = true): Aggregate[] {
  return rows.map((row, index) => {
    const rank = index + 1;
    let outcome: Aggregate["outcome"] = "";
    if (enabled && rules?.advanceThroughRank && rank <= rules.advanceThroughRank) outcome = "advance";
    else if (enabled && rules?.stayThroughRank && rank <= rules.stayThroughRank) outcome = "stay";
    else if (enabled && (rules?.advanceThroughRank || rules?.stayThroughRank)) outcome = "down";
    return { ...row, rank, outcome };
  });
}

function aggregateStandings(matches: LegacyMatch[]): Aggregate[] {
  const totals = new Map<string, ApexStanding>();
  for (const match of matches) {
    for (const standing of match.apex?.standings ?? []) {
      const key = standing.teamId || standing.team.toLocaleLowerCase();
      const current = totals.get(key) ?? { ...standing, rank: 0, gamesPlayed: 0, placementPoints: 0, kills: 0, totalPoints: 0, matchPointEligible: false };
      current.gamesPlayed += standing.gamesPlayed;
      current.placementPoints += standing.placementPoints;
      current.kills += standing.kills;
      current.totalPoints += standing.totalPoints;
      current.matchPointEligible ||= standing.matchPointEligible;
      totals.set(key, current);
    }
  }
  const rows = [...totals.values()].sort((left, right) => right.totalPoints - left.totalPoints || right.kills - left.kills || left.team.localeCompare(right.team));
  return withOutcomes(rows, matches[0]?.apex);
}

function entryForTeam(entries: ApexGameEntry[], row: ApexStanding) {
  return entries.find((entry) => entry.teamId === row.teamId || entry.team.toLocaleLowerCase() === row.team.toLocaleLowerCase());
}

function matchPointGame(details: ApexSeriesDetails, row: ApexStanding) {
  if (!details.matchPointThreshold) return "";
  let total = 0;
  for (const game of details.games) {
    if (total >= details.matchPointThreshold) return `Game ${game.number}`;
    total += entryForTeam(game.entries, row)?.totalPoints ?? 0;
  }
  return total >= details.matchPointThreshold ? "After final game" : "";
}

function ordinal(value: number) {
  const remainder = value % 100;
  if (remainder >= 11 && remainder <= 13) return `${value}th`;
  switch (value % 10) {
    case 1: return `${value}st`;
    case 2: return `${value}nd`;
    case 3: return `${value}rd`;
    default: return `${value}th`;
  }
}

export function ApexStagePanel({ stage, matches, teams }: Props) {
  const isMultiSeries = matches.length > 1;
  const [selectedKey, setSelectedKey] = useState(isMultiSeries ? overallKey : matches[0]?.sourceKey ?? "");
  const hasSelectedMatch = matches.some((match) => match.sourceKey === selectedKey);
  const effectiveKey = isMultiSeries && !hasSelectedMatch ? overallKey : selectedKey;
  const selected = matches.find((match) => match.sourceKey === effectiveKey) ?? matches[0];
  const showOverall = isMultiSeries && effectiveKey === overallKey;
  const aggregate = useMemo(() => aggregateStandings(matches), [matches]);
  const teamByID = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);
  if (!selected?.apex) return <p className="state-panel">Apex lobby data has not been published yet.</p>;

  const details = selected.apex;
  const standings = showOverall ? aggregate : withOutcomes(details.standings, details, !isMultiSeries);
  const games = showOverall ? [] : details.games;
  const totalGames = showOverall ? matches.reduce((total, match) => total + (match.apex?.games.length ?? 0), 0) : games.length;

  return <div className="apex-stage-panel" data-game="apexlegends">
    {isMultiSeries && <div className="apex-series-tabs" role="tablist" aria-label={`${stage} lobby series`}>
      <button role="tab" aria-selected={showOverall} className={showOverall ? "active" : ""} type="button" onClick={() => setSelectedKey(overallKey)}>Overall standings</button>
      {matches.map((match) => <button role="tab" aria-selected={match.sourceKey === effectiveKey} className={match.sourceKey === effectiveKey ? "active" : ""} type="button" key={match.sourceKey} onClick={() => setSelectedKey(match.sourceKey)}>{match.round}</button>)}
    </div>}

    <div className="apex-stage-summary">
      <div><span className="eyebrow">{showOverall ? "Cumulative standings" : selected.round}</span><h3>{showOverall ? stage : `${stage} · ${selected.round}`}</h3></div>
      <div className="apex-stage-meta"><span>{totalGames} games</span>{details.matchPointThreshold ? <strong>Match Point · {details.matchPointThreshold} pts</strong> : null}{!showOverall && <LocalDateTime value={selected.startsAt} status={selected.status} />}</div>
    </div>

    <div className="table-scroll apex-points-scroll"><table className={`standings-table apex-standings-table ${games.length > 0 ? "apex-game-matrix" : ""}`}>
      <thead>{games.length > 0 ? <>
        <tr><th rowSpan={2}>#</th><th rowSpan={2}>Team</th><th rowSpan={2}>Total</th>{details.matchPointThreshold ? <th rowSpan={2}>MP eligible</th> : null}{games.map((game) => <th colSpan={2} key={game.number}>Game {game.number}<small>{game.map || "Map TBD"}</small></th>)}<th rowSpan={2}>Status</th></tr>
        <tr>{games.map((game) => <Fragment key={game.number}><th>Place</th><th>Kills</th></Fragment>)}</tr>
      </> : <tr><th>#</th><th>Team</th><th>Games</th><th>Placement</th><th>Kills</th><th>Total</th><th>Status</th></tr>}</thead>
      <tbody>{standings.map((row) => {
        const team = teamByID.get(row.teamId);
        const status = stage === "Finals" && selected.status === "completed" && row.rank === 1 ? "Champion" : row.outcome === "advance" ? "Advances" : row.outcome === "stay" ? "Survivor" : row.outcome === "down" ? "Eliminated" : row.matchPointEligible ? "Match Point" : "";
        return <tr className={row.outcome ? `apex-standing-${row.outcome}` : ""} key={row.teamId || row.team}>
          <td>{row.rank}</td><td><EntityLink gameId="apexlegends" kind="team" name={row.team} entityId={row.teamId} logoUrl={team?.logoUrl || (row.teamId ? `/media/organization/${row.teamId}` : "")} /></td>
          {games.length > 0 ? <><td><strong>{row.totalPoints}</strong></td>{details.matchPointThreshold ? <td>{matchPointGame(details, row) || "—"}</td> : null}{games.map((game) => {
            const entry = entryForTeam(game.entries, row);
            return <Fragment key={`${game.number}-${row.teamId || row.team}`}><td className={entry ? `apex-placement apex-placement-${Math.min(entry.placement, 5)}` : ""} title={entry ? `${entry.totalPoints} points in this game` : "Did not play"}>{entry ? ordinal(entry.placement) : "—"}</td><td>{entry?.kills ?? "—"}</td></Fragment>;
          })}</> : <><td>{row.gamesPlayed}</td><td>{row.placementPoints}</td><td>{row.kills}</td><td><strong>{row.totalPoints}</strong></td></>}
          <td>{status && <span className={`apex-status apex-status-${row.outcome || "matchpoint"}`}>{status}</span>}</td>
        </tr>;
      })}</tbody>
    </table></div>
    {showOverall && <p className="apex-series-hint">Choose a lobby above to inspect every game’s placement and kills.</p>}
  </div>;
}
